import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Users, TrendingUp } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Helmet } from 'react-helmet-async';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CACCalculator = () => {
  const [marketingCosts, setMarketingCosts] = useState(10000);
  const [salesCosts, setSalesCosts] = useState(5000);
  const [newCustomers, setNewCustomers] = useState(100);
  const [cac, setCac] = useState(0);

  useEffect(() => {
    calculateCAC();
  }, [marketingCosts, salesCosts, newCustomers]);

  const calculateCAC = () => {
    const totalCosts = marketingCosts + salesCosts;
    const calculatedCac = newCustomers > 0 ? totalCosts / newCustomers : 0;
    setCac(parseFloat(calculatedCac.toFixed(2)));
  };

  const chartData = {
    labels: ['Marketing Costs', 'Sales Costs', 'CAC'],
    datasets: [
      {
        label: 'Amount ($)',
        data: [marketingCosts, salesCosts, cac],
        backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)', 'rgba(75, 192, 192, 0.6)'],
      },
    ],
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cac-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>CAC Calculator: Free Customer Acquisition Cost Calculator Tool</title>
        <meta name="description" content="Calculate your Customer Acquisition Cost (CAC) instantly with our free calculator. Optimize your marketing spend and improve customer acquisition efficiency." />
        <meta name="keywords" content="CAC calculator, customer acquisition cost, marketing metrics, customer acquisition calculator, marketing ROI" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free CAC Calculator: Measure Your Customer Acquisition Cost" />
        <meta property="og:description" content="Calculate your Customer Acquisition Cost instantly. Optimize your marketing spend with our free CAC calculator." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free CAC Calculator: Measure Your Customer Acquisition Cost" />
        <meta property="twitter:description" content="Calculate your Customer Acquisition Cost instantly. Optimize your marketing spend with our free CAC calculator." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "CAC Calculator",
            "description": "Free calculator to measure Customer Acquisition Cost for marketing campaigns",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Customer Acquisition Cost",
              "Marketing cost tracking",
              "Sales cost tracking", 
              "Customer acquisition metrics",
              "CAC optimization tips"
            ]
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        CAC (Customer Acquisition Cost) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Marketing Costs</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={marketingCosts}
                  onChange={(e) => setMarketingCosts(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sales Costs</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={salesCosts}
                  onChange={(e) => setSalesCosts(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">New Customers Acquired</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={newCustomers}
                  onChange={(e) => setNewCustomers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${cac}
            <span className="text-xl text-gray-500 ml-2">per customer</span>
          </div>
          <p className="text-gray-600">Customer Acquisition Cost (CAC)</p>
          <div className="mt-6 h-64">
            <Bar 
              data={chartData} 
              options={{ 
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return `$${context.raw}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '$' + value;
                      }
                    }
                  }
                }
              }} 
            />
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600 mb-4">
          Your Customer Acquisition Cost (CAC) is ${cac}. This means you're spending an average of ${cac} to acquire each new customer. 
          {cac > 100 
            ? " Your CAC is relatively high - consider implementing cost optimization strategies." 
            : cac > 50 
              ? " Your CAC is moderate - there may be opportunities for optimization." 
              : " Your CAC is relatively low - your acquisition strategy appears cost-efficient."}
        </p>
        <p className="text-gray-600 mb-4">
          Remember to compare your CAC with your Customer Lifetime Value (CLV). A healthy CLV:CAC ratio should be at least 3:1, meaning your customers should generate at least 3 times more value than it costs to acquire them.
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Improve CAC</h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-700">Marketing Optimization</h4>
            <ul className="list-disc list-inside text-gray-600">
              <li>Optimize your marketing and sales funnels</li>
              <li>Use data analytics to identify and target high-value customer segments</li>
              <li>Implement A/B testing to improve conversion rates</li>
              <li>Focus on channels with the lowest acquisition costs</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-700">Customer Retention</h4>
            <ul className="list-disc list-inside text-gray-600">
              <li>Focus on customer retention to reduce the need for constant acquisition</li>
              <li>Implement a strong onboarding process</li>
              <li>Develop a customer loyalty program</li>
              <li>Regularly collect and act on customer feedback</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-700">Long-term Strategies</h4>
            <ul className="list-disc list-inside text-gray-600">
              <li>Invest in content marketing and SEO for organic growth</li>
              <li>Build a referral program to leverage word-of-mouth marketing</li>
              <li>Develop partnerships and co-marketing opportunities</li>
              <li>Focus on building a strong brand presence</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CACCalculator;
